// src/pages/MyAccount/MyAccount.jsx
// FRONT OFFICE - Enhanced My Account page with order payments and cancellation

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaClipboardList,
  FaMapMarkerAlt,
  FaSignOutAlt,
  FaWhatsapp,
  FaCreditCard,
  FaEye,
  FaTimesCircle
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useOrders } from '../../contexts/OrderContext';
import { getUserProfile, updateUserProfile } from '../../services/UserService';
import { updateOrderStatus, ORDER_STATUS } from '../../services/OrderService';
import { logout } from '../../services/AuthService';
import './MyAccount.css';

const MyAccount = () => {
  const { currentUser } = useAuth();
  const { userOrders, refreshOrders } = useOrders();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', {
        state: {
          from: '/my-account',
          message: 'Please log in to view your account'
        }
      });
      return;
    }

    const loadUserData = async () => {
      try {
        setLoading(true);
        const profile = await getUserProfile(currentUser.uid);
        setUserData(profile);

        // Initialize form data with user profile
        setFormData({
          name: profile.name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Refresh orders when user data is loaded
        refreshOrders();
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load your account data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser, navigate, refreshOrders]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setUpdateError('');
      setUpdateSuccess('');

      // Validate passwords if trying to change password
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          setUpdateError('New passwords do not match');
          setLoading(false);
          return;
        }

        if (formData.newPassword.length < 6) {
          setUpdateError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        // TODO: Implement password change
        // This requires re-authentication which we'll skip for now
      }

      // Update user profile
      await updateUserProfile({
        name: formData.name,
        phone: formData.phone
      });

      // Refresh user data
      const updatedProfile = await getUserProfile(currentUser.uid);
      setUserData(updatedProfile);

      setUpdateSuccess('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setUpdateError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to log out. Please try again.');
    }
  };

  const handleCancelOrder = (order) => {
    setCancellingOrder(order);
    setShowCancelModal(true);
  };

  const confirmCancelOrder = async () => {
    try {
      setLoading(true);
      await updateOrderStatus(cancellingOrder.id, ORDER_STATUS.CANCELLED);
      await refreshOrders();
      setShowCancelModal(false);
      setCancellingOrder(null);
      setUpdateSuccess('Order cancelled successfully');
    } catch (err) {
      console.error('Error cancelling order:', err);
      setUpdateError('Failed to cancel order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePayment = (order) => {
    // Create payment completion message with order details
    const itemsList = order.items 
      ? order.items.map(item => 
          `${item.product?.name} (${item.quantity} × €${item.price?.toFixed(2) || '0.00'})`
        ).join('\n- ')
      : 'No items available';
    
    const message = `Hello, I would like to complete payment for my order ${order.id} totaling €${order.total.toFixed(2)}.\n\nOrder Details:\n- ${itemsList}\n\nShipping Address: ${order.shippingAddress || 'Not specified'}`;
    
    // WhatsApp number
    const whatsappNumber = '+491632055734';
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Loading state
  if (loading && !userData) {
    return (
      <div className="my-account-page">
        <div className="account-loading">
          <div className="spinner"></div>
          <p>Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-account-page">
      <div className="account-header">
        <div className="container">
          <h1 className="account-title">My Account</h1>
          <p className="account-subtitle">
            {userData ? `Welcome back, ${userData.name}!` : 'Manage your account'}
          </p>
        </div>
      </div>

      <div className="container">
        {updateSuccess && <div className="account-success-message">{updateSuccess}</div>}
        {updateError && <div className="account-error-message">{updateError}</div>}

        <div className="account-dashboard">
          <div className="dashboard-sidebar">
            <div className="user-profile-card">
              <div className="user-avatar">
                {userData?.name ? userData.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
              </div>
              <div className="user-info">
                <h3>{userData?.name || 'User'}</h3>
                <p>{userData?.email || ''}</p>
              </div>
            </div>
            
            <ul className="dashboard-menu">
              <li>
                <button
                  className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                  onClick={() => setActiveTab('dashboard')}
                >
                  <i className="icon-dashboard"></i> Dashboard
                </button>
              </li>
              <li>
                <button
                  className={`menu-item ${activeTab === 'orders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('orders')}
                >
                  <FaClipboardList /> Orders
                </button>
              </li>
              <li>
                <button
                  className={`menu-item ${activeTab === 'addresses' ? 'active' : ''}`}
                  onClick={() => setActiveTab('addresses')}
                >
                  <FaMapMarkerAlt /> Addresses
                </button>
              </li>
              <li>
                <button
                  className={`menu-item ${activeTab === 'account' ? 'active' : ''}`}
                  onClick={() => setActiveTab('account')}
                >
                  <FaUser /> Account Details
                </button>
              </li>
              <li>
                <button
                  className="menu-item logout"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt /> Logout
                </button>
              </li>
            </ul>
          </div>

          <div className="dashboard-content">
            {error && <div className="account-error">{error}</div>}
            
            {activeTab === 'dashboard' && (
              <div className="dashboard-welcome">
                <div className="welcome-header">
                  <h2>Dashboard</h2>
                  <p className="welcome-message">
                    Hello {userData?.name} (not {userData?.name}? <button onClick={handleLogout} className="logout-link">Log out</button>)
                  </p>
                </div>
                
                <div className="dashboard-overview">
                  <div className="overview-card">
                    <div className="overview-icon orders">
                      <FaClipboardList />
                    </div>
                    <div className="overview-info">
                      <h3>Orders</h3>
                      <p>{userOrders?.length || 0} total</p>
                      <button onClick={() => setActiveTab('orders')}>View All</button>
                    </div>
                  </div>
                  
                  <div className="overview-card">
                    <div className="overview-icon addresses">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="overview-info">
                      <h3>Addresses</h3>
                      <p>{userData?.addresses?.length || 0} saved</p>
                      <button onClick={() => setActiveTab('addresses')}>Manage</button>
                    </div>
                  </div>
                </div>

                <div className="recent-activity">
                  <div className="activity-section">
                    <h3>Recent Orders</h3>
                    {userOrders && userOrders.length > 0 ? (
                      <div className="activity-list">
                        {userOrders.slice(0, 3).map(order => (
                          <div key={order.id} className="activity-item">
                            <div className="activity-icon">
                              <FaClipboardList />
                            </div>
                            <div className="activity-details">
                              <h4>Order {order.id.substring(0, 8)}</h4>
                              <p>Placed on {formatDate(order.createdAt)} • <span className={`status-text ${order.status.toLowerCase()}`}>{order.status}</span></p>
                            </div>
                            <div className="activity-amount">
                              €{order.total.toFixed(2)}
                            </div>
                            <div className="activity-actions">
                              <Link to={`/order/${order.id}`} className="btn btn-sm btn-outline">
                                <FaEye /> View
                              </Link>
                              {(order.status === ORDER_STATUS.PENDING || order.paymentStatus === 'pending') && (
                                <button 
                                  onClick={() => handleCompletePayment(order)} 
                                  className="btn btn-sm btn-primary"
                                >
                                  <FaWhatsapp /> Pay
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-activity">No orders yet.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="orders-section">
                <div className="section-header">
                  <h2>Orders</h2>
                </div>
                
                {userOrders && userOrders.length > 0 ? (
                  <div className="orders-list">
                    {userOrders.map(order => (
                      <div key={order.id} className="order-card">
                        <div className="order-header">
                          <div className="order-id">
                            <h3>Order {order.id.substring(0, 8)}</h3>
                            <span className={`order-status ${order.status.toLowerCase()}`}>
                              {order.status}
                            </span>
                          </div>
                          <div className="order-date">
                            Placed on {formatDate(order.createdAt)}
                          </div>
                        </div>
                        <div className="order-body">
                          <div className="order-details">
                            <div className="order-payment-status">
                              <span>Payment Status:</span>
                              <span className={`payment-status ${order.paymentStatus || 'pending'}`}>
                                {order.paymentStatus || 'Pending'}
                              </span>
                            </div>
                            <div className="order-total">
                              <span>Total:</span>
                              <span className="amount">€{order.total.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="order-actions">
                            <Link to={`/order/${order.id}`} className="btn btn-outline">
                              <FaEye /> View Details
                            </Link>
                            
                            {(order.status === ORDER_STATUS.PENDING || order.paymentStatus === 'pending') && (
                              <>
                                <button 
                                  onClick={() => handleCompletePayment(order)} 
                                  className="btn btn-primary"
                                >
                                  <FaWhatsapp /> Complete Payment
                                </button>
                                <button
                                  onClick={() => handleCancelOrder(order)}
                                  className="btn btn-danger"
                                >
                                  <FaTimesCircle /> Cancel Order
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <i className="icon-empty-orders"></i>
                    <h3>No orders yet</h3>
                    <p>You haven't placed any orders yet. Start shopping to see them here.</p>
                    <Link to="/shop" className="btn btn-primary">Start Shopping</Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'addresses' && (
              <div className="addresses-section">
                <div className="section-header">
                  <h2>Addresses</h2>
                  <button className="btn btn-primary">Add New Address</button>
                </div>
                
                {userData?.addresses && userData.addresses.length > 0 ? (
                  <div className="addresses-grid">
                    {userData.addresses.map((address) => (
                      <div key={address.id} className="address-card">
                        <div className="address-header">
                          <h3>{address.type} Address</h3>
                          <div className="address-actions">
                            <button className="btn-icon edit">
                              <i className="icon-edit"></i>
                            </button>
                            <button className="btn-icon delete">
                              <i className="icon-delete"></i>
                            </button>
                          </div>
                        </div>
                        <div className="address-content">
                          <p className="address-name">{address.name}</p>
                          <p className="address-street">{address.street}</p>
                          <p className="address-city">{address.city}, {address.state} {address.zip}</p>
                          <p className="address-country">{address.country}</p>
                        </div>
                        <div className="address-footer">
                          {address.type === "Shipping" && (
                            <button className="btn btn-small">
                              Set as Default
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <i className="icon-empty-addresses"></i>
                    <h3>No addresses added</h3>
                    <p>You haven't added any addresses yet.</p>
                    <button className="btn btn-primary">Add New Address</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'account' && (
              <div className="account-section">
                <h2>Account Details</h2>
                
                {updateSuccess && <div className="update-success">{updateSuccess}</div>}
                {updateError && <div className="update-error">{updateError}</div>}
                
                <form className="account-form" onSubmit={handleUpdateProfile}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled
                      />
                      <small>Email cannot be changed</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-divider">
                    <span>Password Change</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <input 
                      type="password" 
                      id="currentPassword" 
                      name="currentPassword" 
                      value={formData.currentPassword}
                      onChange={handleChange}
                      placeholder="Leave blank to keep unchanged" 
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input 
                      type="password" 
                      id="newPassword" 
                      name="newPassword" 
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Leave blank to keep unchanged" 
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input 
                      type="password" 
                      id="confirmPassword" 
                      name="confirmPassword" 
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary save-changes-btn"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Cancel Order</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to cancel this order?</p>
              <p>Order {cancellingOrder?.id.substring(0, 8)} - Total: €{cancellingOrder?.total.toFixed(2)}</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowCancelModal(false)}
                disabled={loading}
              >
                Keep Order
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmCancelOrder}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Yes, Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;
